import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents, Polyline} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Constants from "../../../utils/constants";
import './PatrimoineMap.css'
import {helper} from "../../../utils/helper";
import {Icon, divIcon} from 'leaflet';
import CustomTileLayer from "../layout/map/CustomTileLayer.js";
import FullscreenControl from "../map/FullscreenControl.js";
import ConteneurMarkerPopup from "../map/ConteneurMarkerPopup.js";
import {Paper, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions} from "@mui/material";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import L from "leaflet";
import {relevesTerrainService} from "../../../modules/relevesTerrain/relevesTerrain.service";
import {isExploitant} from "../../../utils/roles";
import ModalDelete from "../layout/ModalDelete";
import Grid from "@mui/material/Grid2";

function PatrimoineMap({patrimoine, conteneurs, onConteneurClick, onConteneurUpdate, searchTerm = "", ...props}) {
    const [conteneursData, setConteneurData] = useState(null)
    const [expandedMarkers, setExpandedMarkers] = useState({});
    const [selectedConteneur, setSelectedConteneur] = useState(null);
    const [mapCenter, setMapCenter] = useState(null);
    const [mapZoom, setMapZoom] = useState(13);
    const mapRef = useRef(null);
    const [editingConteneur, setEditingConteneur] = useState(null);
    const [editingPosition, setEditingPosition] = useState(null);
    const canUseGps = useMemo(() => (isExploitant()), []);
    const [gpsMode, setGpsMode] = useState(() => {
        try {
            const v = window.localStorage.getItem("patrimoine_map_color_mode") === 'gps';
            return canUseGps ? v : false;
        } catch (e) {
            return false;
        }
    });
    const effectiveGpsMode = canUseGps && gpsMode;
    const [releves, setReleves] = useState([]);
    const [relevesLoading, setRelevesLoading] = useState(false);

    // Modals state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [releveToDelete, setReleveToDelete] = useState(null);
    const [applyDialogOpen, setApplyDialogOpen] = useState(false);
    const [releveToApply, setReleveToApply] = useState(null);
    const [busy, setBusy] = useState(false);

    const handleDeleteReleve = useCallback((releve) => {
        if (!releve?.id) return;
        setReleveToDelete(releve);
        setDeleteDialogOpen(true);
    }, []);

    const confirmDeleteReleve = useCallback(async () => {
        if (!releveToDelete?.id) return;
        try {
            setBusy(true);
            await relevesTerrainService.deleteReleve(releveToDelete.id);
            setReleves((prev) => (prev || []).filter((r) => r.id !== releveToDelete.id));
            setDeleteDialogOpen(false);
            setReleveToDelete(null);
        } catch (e) {
            console.error(e);
            // Optionally show an inline error UI, but avoid alerts as requested
        } finally {
            setBusy(false);
        }
    }, [releveToDelete]);

    const handleApplyReleve = useCallback((releve) => {
        if (!releve?.id || !selectedConteneur?.id) return;
        setReleveToApply(releve);
        setApplyDialogOpen(true);
    }, [selectedConteneur]);

    const confirmApplyReleve = useCallback(async () => {
        if (!releveToApply?.id || !selectedConteneur?.id) return;
        try {
            setBusy(true);
            await relevesTerrainService.applyReleveToConteneur(selectedConteneur.id, releveToApply.id);
            // Reload the current page to reflect updated coordinates as requested
            window.location.reload();
        } catch (e) {
            console.error(e);
            // Avoid alerts as requested
        } finally {
            setBusy(false);
            setApplyDialogOpen(false);
            setReleveToApply(null);
        }
    }, [releveToApply, selectedConteneur]);


    const calculateCenter = useCallback((conteneurs) => {
        const totalLat = conteneurs.reduce((acc, c) => acc + parseFloat(c.latitude), 0);
        const totalLng = conteneurs.reduce((acc, c) => acc + parseFloat(c.longitude), 0);
        const avgLat = totalLat / conteneurs.length;
        const avgLng = totalLng / conteneurs.length;
        return [avgLat, avgLng];
    }, []);

    useEffect(() => {
        if (conteneurs && conteneurs.length > 0) {
            const newCenter = calculateCenter(conteneurs);
            setMapCenter(newCenter);
        } else if (patrimoine.isUnipatrimoine) {
            setMapCenter([patrimoine.latitude, patrimoine.longitude]);
        }
    }, [conteneurs, patrimoine, calculateCenter]);

    useEffect(() => {
        if (conteneurs) {
            const data = conteneurs.reduce((acc, conteneur) => {
                const key = `${conteneur.latitude}/${conteneur.longitude}`;
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(conteneur);
                return acc;
            }, {});
            setConteneurData(data);
        }
    }, [conteneurs]);

    useEffect(() => {
        if (mapRef.current && mapCenter) {
            mapRef.current.setView(mapCenter, mapZoom);
        }
    }, [mapCenter, mapZoom]);

    const getAverageConteneurStatusIcon = useMemo(() => (conteneurs) => {
        if (!conteneurs.length) return helper.getConteneurStatusIcon(1);
        const highestStatus = Math.max(...conteneurs.map(c => c.status || 0));
        return helper.getConteneurStatusIcon(highestStatus);
    }, []);

    const getConteneurStatusIcon = (c) => {
        return helper.getConteneurStatusIcon(c.status)
    }

    const gpsColorHex = (name) => {
        switch ((name || '').toLowerCase()) {
            case 'green':
                return Constants.COLOR_THEME_GREEN || '#7bb421';
            case 'orange':
                return Constants.COLOR_THEME_ORANGE || '#e67e22';
            case 'red':
                return Constants.COLOR_THEME_RED || '#B22222';
            case 'grey':
            default:
                return '#808080';
        }
    };

    const getAggregatedGpsColorName = (markers) => {
        if (!markers || markers.length === 0) return 'grey';
        const priority = { grey: 0, green: 1, orange: 2, red: 3 };
        let best = 'grey';
        for (const m of markers) {
            const col = (m?.gpsStatus?.color || 'grey').toLowerCase();
            if ((priority[col] ?? 0) > priority[best]) best = col;
        }
        return best;
    };

    const matchingIds = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term || !conteneurs) return null;
        const ids = new Set();
        for (const c of conteneurs) {
            const fields = [c.nomenclature, c.adresse, c.commune, c.conteneurType, c.dechetType, c.ordreCsv, c.status];
            if (fields.some((f) => f != null && String(f).toLowerCase().includes(term))) {
                ids.add(c.id);
            }
        }
        return ids;
    }, [searchTerm, conteneurs]);


    useEffect(() => {
        if (!matchingIds || !mapRef.current || !conteneurs) return;
        const matches = conteneurs.filter((c) => matchingIds.has(c.id));
        if (matches.length === 0) return;
        if (matches.length === 1) {
            const m = matches[0];
            const lat = parseFloat(m.latitude);
            const lng = parseFloat(m.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
                mapRef.current.flyTo([lat, lng], Math.max(mapRef.current.getZoom(), 18), {duration: 0.8});
            }
        } else {
            const points = matches
                .map((m) => [parseFloat(m.latitude), parseFloat(m.longitude)])
                .filter(([la, ln]) => !isNaN(la) && !isNaN(ln));
            if (points.length > 0) {
                mapRef.current.fitBounds(L.latLngBounds(points), {padding: [40, 40]});
            }
        }
    }, [matchingIds, conteneurs]);

    function getIcon(markers, count) {
        const isDimmed = matchingIds && !markers.some((m) => matchingIds.has(m.id));
        const dimmedClass = isDimmed ? ' dimmed' : '';
        if (effectiveGpsMode) {
            const colorName = getAggregatedGpsColorName(markers);
            const color = gpsColorHex(colorName);
            // Create a simple colored circle marker with count overlay
            const html = `
                <div style="position:relative; width:${56/1.5}px; height:${76/1.5}px; display:flex; align-items:center; justify-content:center;">
                    <div style="width:28px; height:28px; background:${color}; border:2px solid white; border-radius:50%; box-shadow:0 0 3px rgba(0,0,0,0.4);"></div>
                    <span class="marker-number-div">${count ?? ''}</span>
                </div>`;
            return divIcon({
                className: 'my-div-icon' + dimmedClass,
                html,
                iconSize: [(56 / 1.5), (76 / 1.5)]
            });
        }
        let imgSrc = getAverageConteneurStatusIcon(markers);
        return divIcon({
            className: 'my-div-icon' + dimmedClass,
            html: `<img class="marker-image" src="${imgSrc}"/>` +
                `<span class="marker-number-div">${count ?? ''}</span>`,
            iconSize: [(56 / 1.5), (76 / 1.5)]
        });
    }

    const MapController = () => {
        const map = useMapEvents({
            click: (e) => {
                if (editingConteneur) {
                    setEditingPosition([e.latlng.lat, e.latlng.lng]);
                }
            },
        });

        useEffect(() => {
            if (!mapRef.current) {
                mapRef.current = map;
            }
        }, [map]);

        return null;
    };

    const onMarkerClick = useCallback((c) => {
        const key = `${c.latitude}/${c.longitude}`;
        const numMarkers = conteneursData[key]?.length || 0;

        if (numMarkers > 1) {
            setExpandedMarkers(prevExpanded => ({
                ...prevExpanded,
                [key]: !prevExpanded[key]
            }));
        }

        setSelectedConteneur(c);
        if (effectiveGpsMode && c?.id) {
            setReleves([]);
            setRelevesLoading(true);
            relevesTerrainService.fetchByConteneur(c.id)
                .then((items) => {
                    const sorted = (items || []).slice().sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setReleves(sorted);
                })
                .catch(() => {
                    setReleves([]);
                })
                .finally(() => setRelevesLoading(false));
        } else {
            setReleves([]);
        }
        //onConteneurClick(conteneursData[key]);
    }, [conteneursData, onConteneurClick, effectiveGpsMode]);

    const handleClosePopup = useCallback(() => {
        setSelectedConteneur(null);
    }, []);

    const handleOnEdit = useCallback((conteneur) => {
        setEditingConteneur(conteneur);
        setEditingPosition([conteneur.latitude, conteneur.longitude]);
    }, []);

    const handleDragEnd = useCallback((event) => {
        if (editingConteneur) {
            const newPosition = event.target.getLatLng();
            setEditingPosition([newPosition.lat, newPosition.lng]);
        }
    }, [editingConteneur]);

    const handleSaveEdit = useCallback(() => {
        if (editingConteneur && editingPosition) {
            editingConteneur.latitude = editingPosition[0];
            editingConteneur.longitude = editingPosition[1];

            onConteneurUpdate(editingConteneur)


            //reset
            setEditingConteneur(null);
            setEditingPosition(null);
        }
    }, [editingConteneur, editingPosition]);

    const handleCancelEdit = useCallback(() => {
        setEditingConteneur(null);
        setEditingPosition(null);
    }, []);


    return (
        <div style={{ position: 'relative' }}>
            {canUseGps && (
                <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
                    <Grid container justifyContent={"center"} spacing={2}>
                        <Grid item>
                            <Button
                                variant={!gpsMode ? "contained" : "outlined"}
                                color="primary"
                                onClick={() => {
                                    try { window.localStorage.setItem("patrimoine_map_color_mode", 'status'); } catch (e) {}
                                    setGpsMode(false);
                                }}
                            >
                                Statut lavage
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant={gpsMode ? "contained" : "outlined"}
                                color="primary"
                                onClick={() => {
                                    try { window.localStorage.setItem("patrimoine_map_color_mode", 'gps'); } catch (e) {}
                                    setGpsMode(true);
                                }}
                            >
                                Relevé terrain
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            )}
            <MapContainer
                ref={mapRef}
                center={mapCenter}
                zoom={mapZoom}
                whenCreated={(map) => {
                    mapRef.current = map;
                }}
                style={{height: "85vh", width: "100%"}}>
                <MapController/>
                <CustomTileLayer
                    maxZoom={19}
                />

                {effectiveGpsMode && selectedConteneur && (releves || []).map((r, idx) => {
                    const rl = [parseFloat(r.latitude), parseFloat(r.longitude)];
                    const cl = [parseFloat(selectedConteneur.latitude), parseFloat(selectedConteneur.longitude)];
                    const label = helper.alphaIndex(idx);
                    const html = `
                        <div style="position:relative; display:flex; align-items:center;">
                            <div style="width:16px;height:16px;background:#213067;border:2px solid white;border-radius:50%;box-shadow:0 0 3px rgba(0,0,0,0.4);color:white;text-align:center">${label}</div>
                        </div>`;
                    return (
                        <React.Fragment key={`releve-${r.id}`}>
                            <Polyline positions={[cl, rl]} pathOptions={{ color: Constants.COLOR_THEME_BLUE, weight: 2, opacity: 0.8 }} />
                            <Marker
                                position={rl}
                                icon={divIcon({
                                    className: 'my-div-icon',
                                    html,
                                    iconSize: [28, 16]
                                })}
                            />
                        </React.Fragment>
                    );
                })}

                {conteneursData && Object.entries(conteneursData).map(([key, markers]) => (
                    <MarkerGroup
                        key={key}
                        markers={markers}
                        isExpanded={expandedMarkers[key]}
                        getIcon={getIcon}
                        onMarkerClick={onMarkerClick}
                        handleClosePopup={handleClosePopup}
                        handleOnEdit={handleOnEdit}
                        editingConteneur={editingConteneur}
                        editingPosition={editingPosition}
                        handleDragEnd={handleDragEnd}
                        gpsMode={effectiveGpsMode}
                        selectedConteneurId={selectedConteneur?.id}
                        selectedReleves={releves}
                        loadingReleves={relevesLoading}
                    />
                ))}
                {patrimoine.isUnipatrimoine &&
                    <Marker
                        icon={new Icon({
                            iconUrl: getConteneurStatusIcon(patrimoine),
                            iconSize: [(56 / 1.5), (76 / 1.5)],
                        })}
                        position={[patrimoine.latitude, patrimoine.longitude]}/>
                }
                <FullscreenControl/>
                {editingConteneur && (
                    <Popup
                        position={editingPosition}
                        onClose={handleCancelEdit}
                    >
                        <Paper className={"marker-popup-container"}
                               sx={{background: 'white', padding: '1rem', width: 200}}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={handleSaveEdit}
                                    >
                                        Sauvegarder
                                    </Button>
                                </Grid>
                                <Grid size={12}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        onClick={handleCancelEdit}
                                    >
                                        Annuler
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Popup>
                )}
            </MapContainer>
            {effectiveGpsMode && selectedConteneur && (
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1100 }}>
                    <Paper elevation={6} sx={{ m: 1, p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, maxHeight: '35vh', overflowY: 'auto', backgroundColor: 'rgba(255,255,255,0.98)' }}>
                        <Grid container spacing={1} alignItems={"flex-start"}>
                            <Grid size={10}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {selectedConteneur?.nomenclature} : {selectedConteneur?.adresse}, {selectedConteneur?.commune}
                                </Typography>
                            </Grid>
                            <Grid size={2} style={{ textAlign: 'right' }}>
                                <Button size="small" onClick={() => { setSelectedConteneur(null); setReleves([]); }}>
                                    <CloseIcon />
                                </Button>
                            </Grid>
                            <Grid size={12}>
                                <Typography sx={{ fontSize: 13 }}>
                                    Type de déchet:
                                    <Chip size='small' label={selectedConteneur?.dechetType} sx={{ ml: 1, ...helper.getColorForDechetType?.(selectedConteneur?.dechetType) }} />
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Typography sx={{ fontSize: 13 }}>
                                    Typologie: <Chip size='small' label={selectedConteneur?.conteneurType} sx={{ ml: 1 }} /> — Quantité: {selectedConteneur?.quantite}
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Typography sx={{ fontWeight: 600, mt: 1 }}>Relevés terrain ({releves?.length || 0})</Typography>
                            </Grid>
                            {relevesLoading && (
                                <Grid size={12}>
                                    <Typography sx={{ fontSize: 13 }}>Chargement…</Typography>
                                </Grid>
                            )}
                            {!relevesLoading && (releves || []).map((r, idx) => {
                                const d = helper.getDistanceMeters(
                                    Number(selectedConteneur.latitude),
                                    Number(selectedConteneur.longitude),
                                    Number(r.latitude),
                                    Number(r.longitude)
                                );
                                const label = helper.alphaIndex(idx);
                                return (
                                    <Grid size={12} key={r.id}>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs>
                                                <Typography sx={{ fontSize: 13 }}>
                                                    <strong>[{label}]</strong> {helper.getFormattedDatetime(r.createdAt)} — lat: {Number(r.latitude).toFixed(6)}, lng: {Number(r.longitude).toFixed(6)} — {helper.formatDistance(d)}  {r.accuracyM && ` — précision ${r.accuracyM} m`}
                                                </Typography>
                                            </Grid>
                                            {canUseGps && (
                                                <Grid item>
                                                    <Button size="small" variant="contained" color="primary" onClick={() => handleApplyReleve(r)}>Appliquer</Button>
                                                </Grid>
                                            )}
                                            {canUseGps && (
                                                <Grid item>
                                                    <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteReleve(r)}>Supprimer</Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Paper>
                </div>
            )}

            {/* Delete confirmation modal */}
            <ModalDelete
                open={deleteDialogOpen}
                onClose={() => { if (!busy) { setDeleteDialogOpen(false); setReleveToDelete(null); } }}
                onDelete={confirmDeleteReleve}
                label={"Voulez-vous supprimer ce relevé terrain ?"}
                objectToDelete={releveToDelete ? (helper.getFormattedDatetime(releveToDelete.createdAt) || `#${releveToDelete.id}`) : ''}
            />

            {/* Apply confirmation modal */}
            <Dialog open={applyDialogOpen} onClose={() => { if (!busy) { setApplyDialogOpen(false); setReleveToApply(null); } }}>
                <DialogTitle>Appliquer le relevé</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Appliquer les coordonnées de ce relevé au conteneur ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { if (!busy) { setApplyDialogOpen(false); setReleveToApply(null); } }} color="error" variant="contained">Annuler</Button>
                    <Button onClick={confirmApplyReleve} color="success" variant="contained" disabled={busy}>Appliquer</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

const MarkerGroup = React.memo(({
                                    markers,
                                    isExpanded,
                                    getIcon,
                                    onMarkerClick,
                                    handleClosePopup,
                                    handleOnEdit,
                                    editingConteneur,
                                    editingPosition,
                                    handleDragEnd,
                                    gpsMode,
                                    selectedConteneurId,
                                    selectedReleves,
                                    loadingReleves
                                }) => {
    const baseMarker = markers[0];
    const baseLat = parseFloat(baseMarker.latitude);
    const baseLng = parseFloat(baseMarker.longitude);

    return markers.map((marker, index) => {
        let lat = baseLat;
        let lng = baseLng;

        if (isExpanded && markers.length > 1) {
            const angle = (2 * Math.PI * index) / markers.length;
            lat = baseLat + Constants.MAP_RADIUS * Math.cos(angle);
            lng = baseLng + (Constants.MAP_RADIUS * Math.sin(angle)) / Math.cos(baseLat / Constants.DEGREES_PER_RADIAN);
        }

        const isEditing = editingConteneur && editingConteneur.id === marker.id;
        const markerPosition = isEditing ? editingPosition : [lat, lng];

        return (
            <Marker
                key={`${marker.id}-${lat}-${lng}`}
                position={markerPosition}
                icon={getIcon(markers, markers[0]?.quantite)}
                eventHandlers={{
                    click: () => onMarkerClick(marker),
                    dragend: handleDragEnd,
                }}
                draggable={isEditing}
            >
                {!isEditing && !gpsMode && (
                    <ConteneurMarkerPopup
                        key={`${marker.id}-${lat}-${lng}-${index}`}
                        conteneur={marker}
                        onClose={handleClosePopup}
                        onEdit={() => handleOnEdit(marker)}
                    />
                )}
            </Marker>
        );
    });
});

export default React.memo(PatrimoineMap);
